import { CaretRightOutlined, CaretDownOutlined } from "@ant-design/icons";
import { Table, Skeleton } from "antd";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";

import { SystemIcon, ZoneIcon } from "../../../assets/icons/systems";
import useBasePath from "../../../hooks/useBasePath";
import EventIcon from "../../Events/EventIcon";

// IMP: data should be formatted as shown belown
// in order to be expandable
// const data = [
//   {
//     key: 1,
//     name: "David's Office",
//     status: "Alert",
//     location: "Main Pipe",
//     note: "",
//     children: [
//       {
//         key: 11,
//         id: 11,
//         name: "Main Flow Sensor",
//         status: "Alert",
//         location: "Waterline",
//         note: "",
//       },
//       {
//         key: 12,
//         id: 12,
//         name: "Consumption Meter",
//         status: "OK",
//         location: "Second Pipe",
//         note: "",
//         children: [
//           {
//             key: 121,
//             name: "Jim Green jr.",
//             age: 25,
//             address: "London No. 3 Lake Park",
//           },
//         ],
//       },
//     ],
//   },
//   {
//     key: 2,
//     id: 2,
//     name: "Liky's Studio",
//     status: "OK",
//     location: "Waterline",
//     note: "",
//     children: [
//       {
//         key: 21,
//         id: 21,
//         name: "Main Flow Sensor",
//         status: "Alert",
//         location: "Waterline",
//         note: "",
//       },
//       {
//         key: 22,
//         id: 22,
//         name: "Consumption Meter",
//         status: "OK",
//         location: "Second Pipe",
//         note: "",
//       },
//     ],
//   },
//   {
//     key: 3,
//     id: 3,
//     name: "Lia's Appartment",
//     status: "Alert",
//     location: "Waterline",
//     note: "",
//   },
// ];

const propTypes = {
  className: PropTypes.string,
  floor: PropTypes.any,
  status: PropTypes.string,
};
const defaultProps = {
  className: "",
  floor: "",
  status: "",
};

function FloorSystemsTable({ className, floor, status }) {
  const navigate = useNavigate();
  const basePath = useBasePath();

  // const { data: floor, status } = usePropertyFloorById(id);

  const formattedData = floor?.Systems?.map((system, index) => {
    const { Devices, Name, ...rest } = system;

    if (Devices?.length > 0) {
      const [systemHub] = Devices.filter(
        (device) => device.DeviceTypePublicName === "Hub",
      );

      const formattedSystemHub = {
        key: `${index + 1}1`,
        ...systemHub,
      };

      const devicesWithZone = Devices?.filter(
        (device) => device.Zone && device.DeviceTypePublicName !== "Hub",
      );

      const allZonesFromDevices = devicesWithZone
        ?.map((device) => {
          const { Zone } = device;
          return Zone;
        })
        .filter((zone) => zone);

      const uniqueZoneIds = [
        ...new Set(allZonesFromDevices.map((zone) => zone.Id)),
      ];

      const formattedZones = uniqueZoneIds.map((zoneId, zoneIndex) => {
        const [zone] = allZonesFromDevices.filter((zone) => zone.Id === zoneId);

        const zoneDevices = devicesWithZone
          .map((device, deviceIndex) =>
            device.Zone.Id === zoneId
              ? {
                  // zoneIndex + 2 -> zero based + extra 1 for hub
                  key: `${index + 1}${zoneIndex + 2}${deviceIndex + 1}`,
                  ...device,
                }
              : null,
          )
          .filter((device) => device);

        return {
          key: `${index + 1}${zoneIndex + 2}`,

          ZoneName: zone.Name,
          Status: zone.Status,
          children: zoneDevices,
        };
      });

      const devicesWithoutZone = Devices?.filter(
        (device) => !device.Zone && device.DeviceTypePublicName !== "Hub",
      );

      const formattedDevicesWithoutZone = devicesWithoutZone.map(
        (device, deviceIndex) => {
          return {
            key: `${index + 1}${1 + uniqueZoneIds.length + 1 + deviceIndex}`,
            ...device,
          };
        },
      );

      return {
        key: index + 1,
        SystemName: Name,
        ...rest,
        children: [
          formattedSystemHub,
          ...formattedZones,
          ...formattedDevicesWithoutZone,
        ],
      };
    }

    return {
      key: index + 1,
      SystemName: Name,
      ...rest,
    };
  });

  const columns = [
    {
      title: "Name",
      render: (record) => (
        <div className="inline-flex items-center">
          {record?.ZoneName && <ZoneIcon className="mr-2" />}
          {record?.SystemName && (
            <SystemIcon
              className={`${record?.Status.toLowerCase()}-system mr-2`}
            />
          )}
          {record?.DeviceTypePublicName && (
            <EventIcon
              key={record?.Id}
              iconName={record?.DeviceTypePublicName}
              className={`${record?.Severity.toLowerCase()}-event mr-2`}
              defaultIcon={<span>*icon*</span>}
            />
          )}

          {record?.Name || // custom given device name
            record?.DeviceTypePublicName || // generic device name
            record?.ZoneName || // zone name
            record?.SystemName || // system name
            "-"}
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "Status",
      render: (Status) => <span>{Status || "-"}</span>,
    },
    {
      title: "SerialNumber",
      dataIndex: "SerialNumber",
      render: (SerialNumber) => <span>{SerialNumber || "-"}</span>,
    },
  ];

  // TODO: refactor to use CustomTable
  // columns for skeleton table (loading state)
  const ghostTableColumns = columns.map((column) => {
    return {
      ...column,
      render: function renderPlaceholder() {
        return <Skeleton key={column.key} active paragraph={false} />;
      },
    };
  });

  // data for skeleton table (loading state)
  const ghostTableData = [...Array(5)].map((_, index) => ({
    key: `key${index}`,
  }));

  return (
    <Table
      dataSource={status === "loading" ? ghostTableData : formattedData}
      columns={status === "loading" ? ghostTableColumns : columns}
      rowKey="key"
      pagination={{ hideOnSinglePage: true }}
      className={`floor-systems-table ${className}`}
      size="middle"
      expandable={{
        // eslint-disable-next-line react/no-unstable-nested-components, no-unused-vars
        expandIcon: ({ expanded, onExpand, record }) => {
          const isRecordExpandable = !!record?.children;
          if (isRecordExpandable) {
            return expanded ? (
              <CaretDownOutlined className="mr-2 inline-flex items-center" />
            ) : (
              <CaretRightOutlined className="mr-2 inline-flex items-center" />
            );
          }

          return null;
        },
        expandRowByClick: true,
        indentSize: 44,
      }}
      // rowClassName={(record) =>
      //   record?.id === floor?.Id ? "bg-[#51566E]" : ""
      // }
      onRow={(record) => {
        if (!record?.DeviceTypePublicName) {
          return null;
        }

        return {
          onClick: () => {
            navigate({
              pathname:
                basePath === "/property-overview/property/floor"
                  ? `device-details/${record.UniqueId}`
                  : `floor/device-details/${record.UniqueId}`,
            });
          },
        };
      }}
    />
  );
}

FloorSystemsTable.propTypes = propTypes;
FloorSystemsTable.defaultProps = defaultProps;

export default FloorSystemsTable;
